import { Switch } from "@mui/material";
import { CloseGreyIcon } from "../../../assets/icons";
import { CreateLocalEventDTO } from "../../../models/localEvent/localEvent.request";
import { useState } from "react";
import { useNotification } from "../../toster-notification/NotificationContext";
import { useParams } from "react-router-dom";
import { localEventService } from "../../../services/localEventService";
import { nameToSlug } from "../../../utils/slugService";
import Loader from "../../shared/Loader";

type ErrorState = Partial<Record<keyof CreateLocalEventDTO, string>>;

interface AddLocalEventViewProps {
    localId: string;
    onClose: () => void;
}

export default function AddLocalEventView({ onClose, localId }: AddLocalEventViewProps) {

    const [newLocalEvent, setNewLocalEvent] = useState<CreateLocalEventDTO>({
        title: "",
        slug: "",
        description: "",
        reservationUrl: "",
        priceText: "",
        startsAtUtc: new Date(),
        endsAtUtc: new Date(),
        visibleFromUtc: new Date(),
        visibleUntilUtc: new Date(),
        imageFile: undefined,
        isPublished: false,
        isCancelled: false,
        isFeatured: false
    });
    const [errors, setErrors] = useState<ErrorState>({});

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();


    const handleCreateLocalEvent = async () => {

        const isValid = validate();
        if (!isValid) {
            notify("error", "Proverite podatke koje ste uneli.");
            return;
        }

        setLoading(true);
        try {

            const formData = new FormData();
            formData.append("title", newLocalEvent.title);
            formData.append("slug", newLocalEvent.slug);
            formData.append("description", newLocalEvent.description || "");
            formData.append("reservationUrl", newLocalEvent.reservationUrl || "");
            formData.append("priceText", newLocalEvent.priceText || "");
            formData.append("startsAtUtc", newLocalEvent.startsAtUtc.toISOString());
            formData.append("endsAtUtc", newLocalEvent.endsAtUtc.toISOString());
            formData.append("visibleFromUtc", newLocalEvent.visibleFromUtc.toISOString());
            formData.append("visibleUntilUtc", newLocalEvent.visibleUntilUtc.toISOString());
            formData.append("isPublished", String(newLocalEvent.isPublished));
            formData.append("isCancelled", String(newLocalEvent.isCancelled));
            formData.append("isFeatured", String(newLocalEvent.isFeatured));

            if (newLocalEvent.imageFile) {
                formData.append("imageFile", newLocalEvent.imageFile);
            }

            const response = await localEventService.Create(localId, formData);

            if (response.success) {
                notify("success", "Događaj je uspešno kreiran");
                onClose();
            } else {
                notify("error", "Došlo je do greške prilikom kreiranja događaja");
            }
        } catch (error) {
            notify("error", "Došlo je do greške prilikom kreiranja događaja");
        } finally {
            setLoading(false);
        }
    }

    const getError = (field: keyof CreateLocalEventDTO) =>
        errors[field] ? <div className="error-text">{errors[field]}</div> : null;

    const updateField = <K extends keyof CreateLocalEventDTO>(
        field: K,
        value: CreateLocalEventDTO[K]
    ) => {
        setNewLocalEvent((previousState) => ({
            ...previousState,
            [field]: value
        }));

        setErrors((previousErrors) => ({
            ...previousErrors,
            [field]: undefined
        }));
    };

    const validate = (): boolean => {
        const validationErrors: ErrorState = {};

        const title = newLocalEvent.title.trim();
        const slug = newLocalEvent.slug.trim();
        const description = newLocalEvent.description?.trim() ?? "";

        // Title validacija
        if (!title) {
            validationErrors.title = "Naziv događaja je obavezan.";
        } else if (title.length > 100) {
            validationErrors.title =
                "Naziv događaja ne sme imati više od 100 karaktera.";
        }

        // Slug validacija
        if (!slug) {
            validationErrors.slug = "Slug je obavezan.";
        } else if (slug.length > 50) {
            validationErrors.slug =
                "Slug ne sme imati više od 50 karaktera.";
        }

        // Description nije obavezan, ali ima maksimalnu dužinu
        if (description.length > 500) {
            validationErrors.description =
                "Opis događaja ne sme imati više od 500 karaktera.";
        }

        const startsAt = newLocalEvent.startsAtUtc;
        const endsAt = newLocalEvent.endsAtUtc;
        const visibleFrom = newLocalEvent.visibleFromUtc;
        const visibleUntil = newLocalEvent.visibleUntilUtc;

        const startDateIsValid =
            startsAt instanceof Date &&
            !Number.isNaN(startsAt.getTime());

        const endDateIsValid =
            endsAt instanceof Date &&
            !Number.isNaN(endsAt.getTime());

        const visibleFromDateIsValid =
            visibleFrom instanceof Date &&
            !Number.isNaN(visibleFrom.getTime());

        const visibleUntilDateIsValid =
            visibleUntil instanceof Date &&
            !Number.isNaN(visibleUntil.getTime());

        // Datum početka
        if (!startDateIsValid) {
            validationErrors.startsAtUtc =
                "Datum početka događaja je obavezan.";
        }

        // Datum kraja
        if (!endDateIsValid) {
            validationErrors.endsAtUtc =
                "Datum završetka događaja je obavezan.";
        }

        // Poredimo ih samo ako su oba datuma validna
        if (startDateIsValid && endDateIsValid) {
            if (startsAt.getTime() >= endsAt.getTime()) {
                validationErrors.startsAtUtc =
                    "Početak događaja mora biti pre kraja događaja.";

                validationErrors.endsAtUtc =
                    "Kraj događaja mora biti nakon početka događaja.";
            }
        }

        // Vidljiv od
        if (!visibleFromDateIsValid) {
            validationErrors.visibleFromUtc =
                "Datum početka vidljivosti je obavezan.";
        }

        // Vidljiv do
        if (!visibleUntilDateIsValid) {
            validationErrors.visibleUntilUtc =
                "Datum završetka vidljivosti je obavezan.";
        }

        if (visibleFromDateIsValid && visibleUntilDateIsValid) {
            if (visibleFrom.getTime() >= visibleUntil.getTime()) {
                validationErrors.visibleFromUtc =
                    "Početak vidljivosti mora biti pre kraja vidljivosti.";

                validationErrors.visibleUntilUtc =
                    "Kraj vidljivosti mora biti nakon početka vidljivosti.";
            }
        }

        setErrors(validationErrors);

        return Object.keys(validationErrors).length === 0;
    };

    const uploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setNewLocalEvent({ ...newLocalEvent, imageFile: file });
        }
    }

    const formatDateForInput = (date: Date | undefined) => {
        if (!date || Number.isNaN(date.getTime())) {
            return "";
        }

        const timezoneOffset = date.getTimezoneOffset() * 60_000;
        const localDate = new Date(date.getTime() - timezoneOffset);

        return localDate.toISOString().slice(0, 16);
    };


    if (loading) {
        return <Loader />;
    }

    return (
        <section className="modal-section local-event">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div product">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Novi događaj</div>
                                <div className="description-text">Podaci se prikazuju gostima samo u periodu vidljivosti koji odredite.</div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>
                        <div className="modal-form-block w-form">
                            <form className="modal-form" >

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Naziv događaja *</label>
                                    <input className="form-input grey w-input" placeholder="npr. Veče italijanske kuhinje" type="text" maxLength={100} name="localEventTitle"
                                        value={newLocalEvent.title}
                                        onChange={(e) => {
                                            updateField("title", e.target.value)
                                            updateField("slug", nameToSlug(e.target.value))
                                        }}
                                    />
                                    {getError("title")}
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Slug *</label>
                                    <input className="form-input grey w-input" placeholder="npr. vece-italijanske-kuhinje" type="text" maxLength={50} name="localEventSlug"
                                        value={newLocalEvent.slug} 
                                        onChange={(e) => updateField("slug", e.target.value)}
                                    />
                                    {getError("slug")}
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Opis</label>
                                    <textarea placeholder="Kratak opis događaja" className="form-textarea grey w-input" maxLength={500} 
                                        value={newLocalEvent.description}
                                        onChange={(e) => updateField("description", e.target.value)}
                                    ></textarea>
                                    {getError("description")}
                                </div>

                                <div className="div-block-133">
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Cena / napomena</label>
                                        <input className="form-input grey w-input" placeholder="npr. Slobodan ulaz" type="text" name="localEventPriceText" maxLength={100}
                                            value={newLocalEvent.priceText}
                                            onChange={(e) => updateField("priceText", e.target.value)}
                                        />
                                        {getError("priceText")}
                                    </div>
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Link za rezervaciju</label>
                                        <input className="form-input grey w-input" placeholder="npr. https://example.com/rezervacija" type="text" name="localEventReservationUrl"
                                            value={newLocalEvent.reservationUrl}
                                            onChange={(e) => updateField("reservationUrl", e.target.value)}
                                        />
                                        {getError("reservationUrl")}
                                    </div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Upload slike</label>
                                    <input className="w-input" placeholder="npr. Margarita, Carbonara" type="file"
                                        onChange={uploadImage}
                                    />
                                    <div className="modal-image-div">
                                        <img sizes="(max-width: 767px) 100vw, (max-width: 991px) 95vw, 940px" alt="" loading="lazy" className="modal-image"
                                            src={newLocalEvent.imageFile ? URL.createObjectURL(newLocalEvent.imageFile) : ""}
                                        />
                                        <div className="modal-image-remove-div">
                                            <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-16"
                                                onClick={() => {
                                                    updateField("imageFile", undefined);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {getError("imageFile")}
                                </div>

                                <div className="div-block-133">
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Početak događaja *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(newLocalEvent.startsAtUtc as Date)}
                                            onChange={(e) => updateField("startsAtUtc", new Date(e.target.value))}
                                        />
                                        {getError("startsAtUtc")}
                                    </div>
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Kraj događaja *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(newLocalEvent.endsAtUtc as Date)}
                                            onChange={(e) => updateField("endsAtUtc", new Date(e.target.value))}
                                        />
                                        {getError("endsAtUtc")}
                                    </div>
                                </div>

                                <div className="div-block-133">
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Vidljiv od *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(newLocalEvent.visibleFromUtc as Date)}
                                            onChange={(e) => updateField("visibleFromUtc", new Date(e.target.value))}
                                        />
                                        {getError("visibleFromUtc")}
                                    </div>
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Vidljiv do *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(newLocalEvent.visibleUntilUtc as Date)}
                                            onChange={(e) => updateField("visibleUntilUtc", new Date(e.target.value))}
                                        />
                                        {getError("visibleUntilUtc")}
                                    </div>
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Objavljen</label>
                                    <Switch
                                        checked={newLocalEvent.isPublished}
                                        onChange={(e) => updateField("isPublished", e.target.checked)}
                                    />
                                    {getError("isPublished")}
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Istaknut</label>
                                    <Switch
                                        checked={newLocalEvent.isFeatured}
                                        onChange={(e) => updateField("isFeatured", e.target.checked)}
                                    />
                                    {getError("isFeatured")}
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Otkazan</label>
                                    <Switch
                                        checked={newLocalEvent.isCancelled}
                                        onChange={(e) => updateField("isCancelled", e.target.checked)}
                                    />
                                    {getError("isCancelled")}
                                </div>

                            </form>
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium" onClick={onClose}>
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium" onClick={handleCreateLocalEvent} >
                                <div>Kreiraj</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}